<template>
  <div>
    <gmap-map
      class="map"
      ref="map"
      :center="{ lat: 38.552704, lng: -90.082259 }"
      :zoom="5"
      :options="MapOptions"
    >
    </gmap-map>

    <div class="district-list box" v-if="!isCreating">
      <div class="header">
        <h1 class="title is-4 has-text-centered">Districts</h1>
        <hr>
      </div>

      <div class="list">
        <div :is="SearchCardComponent" v-for="district in districts" :entity="district" :key="district.id" v-if="districts.length > 0">
          <!--button class="button is-warning" slot="controls" @click="onEdit(district)">Edit</button-->
          <span slot="controls">&nbsp;</span>
          <button class="button is-danger" slot="controls" @click="onDelete(district)">Delete</button>
        </div>
      </div>

      <div class="controls">
        <hr>
        <nav class="level">
          <div class="level-left"></div>
          <div class="level-right">
            <div class="level-item">
              <button class="button is-success" @click="load">Refresh</button>
            </div>
            <div class="level-item">
              <button class="button is-primary" @click="onCreate">Create District</button>
            </div>
          </div>
        </nav>
      </div>
    </div>

    <button type="button" class="button is-danger is-action" id="stop-creating-btn" v-if="isCreating" @click="onFinishCreate">
      <fa-icon icon="stop"></fa-icon>
    </button>
  </div>
</template>

<script>
import Library from 'library'
import States from '@/assets/data/states'

export default {
  name: 'DistrictsSearch',

  data () {
    return {
      states: [],
      districts: [],
      isCreating: null,
      district: null
    }
  },

  async mounted () {
    this.load()
  },

  methods: {
    async load () {
      const loader = this.$loading.open()

      try {
        let response = await this.$entity.query.all(this.$type, {}, ['States'])

        const districts = response.data

        this.districts = districts

        response = await this.$entity.query.all('State', {})
        
        if (response && response.data && Array.isArray(response.data)) {
          const states = response.data

          states.forEach((state) => {
            state.dataLayer = new google.maps.Data()

            const feature = States.features.find(f => f.properties.GEO_ID.endsWith(state.fipsCode))

            if (feature) {
              state.dataLayer.addGeoJson(feature)
            }
          })

          this.states = states

          this.districts.forEach(district => {
            const districtColor = Library.Functions.getRandomColor()
            district.states.forEach(state => {
              const entity = this.states.find(s => s.id === state.id)

              entity.dataLayer.setStyle({
                fillColor: districtColor
              })
              entity.dataLayer.setMap(this.$refs.map.$mapObject)
            })
          })
        }
      } catch (e) {
        console.error(e)
        this.$handleError('There was an error loading Regions', e.response.data)
      } finally {
        loader.close()
      }
    },

    async onCreate () {
      try {
        const name = await new Promise((resolve, reject) => {
          this.$dialog.prompt({
            message: 'What is the name of the district?',
            inputAttrs: {
              maxlength: 45
            },
            onConfirm: (value) => resolve(value),
            onCancel: () => resolve(null)
          })
        })

        if (name) {
          this.isCreating = true
          this.district = {
            name,
            states: []
          }

          this.states.forEach(state => {
            state.dataLayer.setMap(null)
          })

          this.states.filter(state => {
            return !this.districts.find(district => district.states.find(s => s.id === state.id))
          }).forEach(state => {
            state.dataLayer.setMap(this.$refs.map.$mapObject)
    
            state.dataLayer.addListener('click', (e) => {
              const index = this.district.states.indexOf(state.id)
    
              if (index !== -1) {
                this.district.states.splice(index, 1)
                state.dataLayer.setStyle(null)
              } else {
                this.district.states.push(state.id)
                state.dataLayer.setStyle({
                  fillColor: 'orange'
                })
              }
            })
          })
        }
      } catch (e) {
        // Swallow Errors
        this.isCreating = false
        this.district = null
      }
    },

    async onDelete (district) {
      this.$dialog.confirm({
        message: `Do you want to delete ${district.name}`,
        onConfirm: async () => {
          const loader = this.$loading.open()
          try {
            await this.$entity.remove(this.$type, district.id);

            const index = this.districts.indexOf(district)
            if (index !== -1) {
              this.districts.splice(index, 1)
            }

            this.states.forEach(state => {
              state.dataLayer.setMap(null)
            })

            await this.load()
          } catch (e) {
            console.error(e)
            this.$handleError('There was an error deleting that district', e.response.data)
          } finally {
            loader.close()
          }
        }
      })
    },

    async onFinishCreate () {
      if (this.district.states.length > 0) {
        const loader = this.$loading.open()
        try {
          const response = await this.$entity.create('District', {
            name: this.district.name
          })

          for (const stateId of this.district.states) {
            await this.$http.put(`/Districts/${response.data.id}/State/${stateId}`)
          }

          this.states.forEach(state => {
            state.dataLayer.setMap(null)
          })

          await this.load()
        } catch (e) {
          console.error(e)
          this.$handleError('There was an error creating your district', e.response.data)
        } finally {
          loader.close()
        }
      }

      this.isCreating = false
      this.district = null
    },

    async onEdit (district) {
      // TODO: Add ability to edit districts
    },
  },

  computed: {
    MapOptions () {
      return {
        fullscreenControl: false,
        streetViewControl: false,
        zoomControlOptions: {
          position: 6 // Left Top
        }
      }
    },

    SearchCardComponent () {
      return Library.Components.SearchCards.District
    }
  }
}
</script>

<style lang="scss" scoped>
  .map {
    position: absolute;
    top: 0px;
    right: 0px;
    left: 0px;
    bottom: 0px;
  }

  .district-list {
    position: absolute;
    top: 1rem;
    right: 1rem;
    bottom: 1rem;
    display: flex;
    flex-direction: column;

    width: 350px;

    .header {
      flex: 0 0 auto;
    }

    .list {
      flex: 1 1 auto;
      overflow-y: auto;
      overflow-x: visible;
      margin-left: -1rem;
      margin-right: -1rem;
      padding: .5rem;
    }

    .controls {
      flex: 0 0 auto;
    }
  }

  .button.is-action {
    width: 4rem;
    height: 4rem;
    border-radius: 2rem;
    font-size: 1.5rem;
  }

  #stop-creating-btn {
    position: absolute;
    right: 3rem;
    bottom: 1rem;
  }
</style>
